import React, { useState, useEffect, useCallback } from 'react';
import { Container, Typography, Button, Box, Alert, Fade, alpha, Tooltip } from '@mui/material';
import { useParams, useNavigate, useMatch } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import api from '../../../services/api';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Groups as PeopleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  Badge as BadgeIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  Work as WorkIcon,
  Cancel as CancelIcon,
  Shield as ShieldIcon,
  AccountCircle as AccountCircleIcon,
  Circle as CircleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { canEditUser as canEditUserAccess, canDeleteUser as canDeleteUserAccess } from '../../../utils/userPermissions';
import { DANGER } from '../../common/SaaSTokens';
import StorageImage from '../../common/StorageImage';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';
import FaceRecognitionStatusChip from '../../common/FaceRecognitionStatusChip';

// ─── Design tokens (shared with RequirementDetails) ───────────────────────────
const NEON       = '#38BDF8';
const NEON2      = '#818CF8';
const NEON3      = '#34D399';
const CORAL      = '#F87171';
const AMBER      = '#FBBF24';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const SURFACE_TOP = '#152640';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';
const TEXT_SUB    = '#94A3B8';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  active:   { color: NEON3,  label: 'Active' },
  inactive: { color: CORAL,  label: 'Inactive' },
};

// ─── Section wrapper (identical to RequirementDetails) ────────────────────────
const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box
    sx={{
      borderRadius: '16px',
      border: `1px solid ${alpha(accent, 0.14)}`,
      background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
      overflow: 'hidden',
      animation: `fadeUp 0.4s ease ${delay}s both`,
      '@keyframes fadeUp': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to:   { opacity: 1, transform: 'translateY(0)' },
      },
    }}
  >
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

// ─── Info row (identical to RequirementDetails) ───────────────────────────────
const InfoRow = ({ icon: Icon, label, value, href, color = NEON, iconColor, emptyLabel, mono }) => {
  const display = value || emptyLabel;
  if (!display) return null;
  const isEmpty = !value && emptyLabel;

  const content = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.9, px: 1.2, borderRadius: '9px',
      transition: 'background 0.15s',
      '&:hover': href && !isEmpty ? { background: alpha(color, 0.06) } : {},
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(iconColor || color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(iconColor || color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : (iconColor || alpha(color, 0.85)) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.88rem',
          color: isEmpty ? TEXT_MUTED : (href ? color : TEXT_BODY),
          fontStyle: isEmpty ? 'italic' : 'normal',
          fontFamily: mono ? "'JetBrains Mono', monospace" : "'DM Sans', sans-serif",
          fontWeight: isEmpty ? 400 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {display}
        </Typography>
      </Box>
      {href && !isEmpty && <OpenInNewIcon sx={{ fontSize: 12, color: alpha(color, 0.45), flexShrink: 0 }} />}
    </Box>
  );

  return (href && !isEmpty)
    ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}>{content}</a>
    : content;
};

// ─── Metric card (identical to RequirementDetails) ────────────────────────────
const MetricCard = ({ label, value, sub, icon: Icon, color = NEON, sx = {} }) => (
  <Box sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
    transition: 'box-shadow 0.25s, border-color 0.25s',
    '&:hover': {
      boxShadow: `0 0 36px ${alpha(color, 0.16)}, inset 0 1px 0 ${alpha(color, 0.18)}`,
      borderColor: alpha(color, 0.35),
    },
    ...sx,
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

// ─── Sensitive data notice ────────────────────────────────────────────────────
const SensitiveNotice = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '8px', background: alpha(AMBER, 0.05), border: `1px solid ${alpha(AMBER, 0.18)}` }}>
    <ShieldIcon sx={{ fontSize: 13, color: alpha(AMBER, 0.7), flexShrink: 0 }} />
    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: alpha(TEXT_BODY, 0.9) }}>
      Sensitive data — visible to authorised users only
    </Typography>
  </Box>
);

// ─── Action buttons ───────────────────────────────────────────────────────────
const ActionButtons = ({ canEditUser, canDeleteUser, onEdit, onDelete }) => {
  if (!canEditUser && !canDeleteUser) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
      {canEditUser && (
        <Button
          startIcon={<EditIcon sx={{ fontSize: 15 }} />}
          onClick={onEdit}
          sx={{
            borderRadius: '10px', px: 2.2, py: 0.65,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
            background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`,
            color: NEON, transition: 'all 0.2s',
            '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` },
          }}
        >Edit</Button>
      )}
      {canDeleteUser && (
        <Button
          startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
          onClick={onDelete}
          sx={{
            borderRadius: '10px', px: 2.2, py: 0.65,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
            background: 'transparent', border: `1.5px solid ${alpha(CORAL, 0.55)}`,
            color: CORAL, transition: 'all 0.2s',
            '&:hover': { background: alpha(CORAL, 0.08), borderColor: CORAL, boxShadow: `0 0 14px ${alpha(CORAL, 0.2)}` },
          }}
        >Delete</Button>
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const UserDetails = () => {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const profileMatch = useMatch({ path: '/profile', end: true });
  const { user: currentUser, initialized } = useAuth();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const effectiveId = paramId ?? (profileMatch && currentUser?.id != null ? String(currentUser.id) : null);
  const numericId = effectiveId != null ? parseInt(effectiveId, 10) : NaN;
  const canEditUser   = Number.isFinite(numericId) ? canEditUserAccess(currentUser, numericId)   : false;
  const canDeleteUser = Number.isFinite(numericId) ? canDeleteUserAccess(currentUser, numericId) : false;
  const canViewIdentification = canEditUser;
  const isActive = (user?.status || 'active') !== 'inactive';
  const statusCfg = STATUS_CONFIG[isActive ? 'active' : 'inactive'];
  const userProfilePicture = user?.profile_picture || user?.profile_picture_url;

  useEffect(() => { injectDetailsPageStyles(); }, []);

  const fetchUserDetails = useCallback(async () => {
    if (effectiveId == null) return;
    try {
      setLoading(true);
      const response = await api.get(`/users/${effectiveId}`);
      setUser(response.data);
      setError(null);
    } catch {
      setError('Failed to fetch user details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [effectiveId]);

  useEffect(() => {
    if (!initialized) return;
    if (!currentUser) { navigate('/login'); return; }
    if (effectiveId == null) return;
    fetchUserDetails();
  }, [effectiveId, currentUser, initialized, fetchUserDetails, navigate]);

  const confirmDelete = async () => {
    if (effectiveId == null) return;
    try {
      const userName = user?.name || 'User';
      await api.delete(`/users/${effectiveId}`);
      setDeleteDialogOpen(false);
      setSuccess(`User "${userName}" has been deleted successfully!`);
      setTimeout(() => navigate('/users'), 2000);
    } catch {
      setError('Failed to delete user. Please try again.');
      setDeleteDialogOpen(false);
    }
  };

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (!initialized || (effectiveId != null && loading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loading message="Loading user details" size={56} variant="primary" />
      </Box>
    );
  }

  if (initialized && !currentUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: '12px', background: alpha('#450a0a', 0.9), color: '#fca5a5', border: '1px solid rgba(248,113,113,0.3)' }}>
          Please log in to view this page.
        </Alert>
      </Container>
    );
  }

  if (error && !user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', background: alpha('#450a0a', 0.9), color: '#fca5a5', border: '1px solid rgba(248,113,113,0.3)' }}>{error}</Alert>
        <Button variant="outlined" onClick={() => window.location.reload()} sx={{ borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, borderColor: alpha(NEON, 0.4), color: NEON }}>
          Try Again
        </Button>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <PersonIcon sx={{ fontSize: 64, color: alpha(NEON, 0.2), mb: 2 }} />
        <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1.6rem', color: alpha(TEXT_BODY, 0.7), mb: 2 }}>User Not Found</Typography>
        <Button onClick={() => navigate('/users')} sx={{ borderRadius: '10px', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, borderColor: alpha(NEON, 0.4), color: NEON }}>
          Back to Users
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>
      <Fade in timeout={400}>
        <Box>
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <Header
            title="User Details"
            subtitle="View user information"
            icon={PeopleIcon}
            showBackButton
            onBackClick={() => navigate(-1)}
            actionButton={
              <ActionButtons
                canEditUser={canEditUser}
                canDeleteUser={canDeleteUser}
                onEdit={() => navigate(`/users/${effectiveId}/edit`)}
                onDelete={() => setDeleteDialogOpen(true)}
              />
            }
          />

          {/* ── Feedback ────────────────────────────────────────────────────── */}
          <AnimatePresence>
            {error && (
              <motion.div key="err" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
                  sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
                />
              </motion.div>
            )}
            {success && (
              <motion.div key="ok" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                <FeedbackMessage message={success} severity="success" onClose={() => setSuccess(null)}
                  sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha(NEON3, 0.08), border: `1px solid ${alpha(NEON3, 0.3)}`, color: NEON3 }}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* ══════════════════════════════════════════════════════════════
                HERO CARD — avatar + name + status + key metrics
                ══════════════════════════════════════════════════════════════ */}
            <Box sx={{
              borderRadius: '16px',
              border: `1px solid ${alpha(NEON, 0.15)}`,
              background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
              p: { xs: 2, md: 2.5 },
              animation: 'fadeUp 0.35s ease both',
              '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
            }}>

              {/* Name row + status badge */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* Avatar / initials */}
                  {userProfilePicture ? (
                    <StorageImage
                      storagePath={userProfilePicture}
                      alt={user.name}
                      sx={{ width: 48, height: 48, borderRadius: '13px', objectFit: 'cover', border: `2px solid ${alpha(NEON, 0.3)}`, flexShrink: 0 }}
                    />
                  ) : (
                    <Box sx={{
                      width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                      background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                      border: `2px solid ${alpha(NEON2, 0.3)}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1.2rem', color: NEON2 }}>
                        {user.name?.charAt(0)?.toUpperCase() || '?'}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                      {user.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                      {user.email}
                      {user.is_admin ? ' · Administrator' : ' · Member'}
                    </Typography>
                  </Box>
                </Box>

                {/* Status + face registration */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignSelf: 'flex-start', justifyContent: 'flex-end' }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.7,
                    px: 1.4, py: 0.5, borderRadius: '20px',
                    background: alpha(statusCfg.color, 0.1),
                    border: `1px solid ${alpha(statusCfg.color, 0.3)}`,
                  }}>
                    <CircleIcon sx={{ fontSize: 7, color: statusCfg.color }} />
                    <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: statusCfg.color, letterSpacing: '0.05em' }}>
                      {statusCfg.label}
                    </Typography>
                  </Box>
                  <FaceRecognitionStatusChip user={user} />
                </Box>
              </Box>

              {/* Metric strip */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <MetricCard
                  label="Role"
                  value={user.is_admin ? 'Administrator' : 'Member'}
                  icon={ShieldIcon}
                  color={user.is_admin ? NEON2 : alpha(NEON, 0.75)}
                />

                <MetricCard
                  label="Management"
                  value={user.management?.name}
                  icon={AdminIcon}
                  color={NEON3}
                />

                <MetricCard
                  label="Department"
                  value={user.department?.name}
                  icon={BusinessIcon}
                  color={NEON}
                />

                <MetricCard
                  label="Bureau"
                  value={user.bureau?.name}
                  icon={WorkIcon}
                  color={NEON2}
                />
              </Box>
            </Box>

            {/* ══════════════════════════════════════════════════════════════
                TWO-COL: Personal Info | Identification (if authorised)
                ══════════════════════════════════════════════════════════════ */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: canViewIdentification ? '1fr 1fr' : '1fr' }, gap: 2 }}>

              <Section title="Personal Information" icon={AccountCircleIcon} accent={NEON} delay={0.1}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <InfoRow icon={PersonIcon} label="Full Name"      value={user.name}   color={NEON} emptyLabel="—" />
                  <InfoRow icon={EmailIcon}  label="Email Address"  value={user.email}  color={NEON} href={`mailto:${user.email}`} emptyLabel="—" />
                  <InfoRow
                    icon={PhoneIcon}
                    label="Phone Number"
                    value={user.phone_number}
                    color={NEON}
                    href={user.phone_number ? `tel:${user.phone_number}` : undefined}
                    emptyLabel="—"
                  />
                  <InfoRow icon={SchoolIcon} label="Course"         value={user.course} color={NEON} emptyLabel="—" />
                </Box>
              </Section>

              {canViewIdentification && (
                <Section title="Identification" icon={BadgeIcon} accent={NEON2} delay={0.14}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    <InfoRow icon={BadgeIcon}      label="Student ID"        value={user.matric_number}   color={NEON2} mono emptyLabel="—" />
                    <InfoRow icon={CreditCardIcon} label="IC Number"         value={user.ic_number}       color={NEON2} mono emptyLabel="—" />
                    <InfoRow icon={CreditCardIcon} label="Passport Number"   value={user.passport_number} color={NEON2} mono emptyLabel="—" />
                    <SensitiveNotice />
                  </Box>
                </Section>
              )}
            </Box>


            {/* ══════════════════════════════════════════════════════════════
                System Information
                ══════════════════════════════════════════════════════════════ */}
            <SystemInformationSection
              createdBy={user.creator?.name}
              createdAt={user.created_at ? new Date(user.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null}
              updatedBy={user.updater?.name}
              updatedAt={user.updated_at ? new Date(user.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null}
              updatedAtLabel="Last Updated"
              delay={0.22}
              accentColor={NEON}
              surfaceUp={SURFACE_UP}
              surfaceMid={SURFACE_MID}
              textBody={TEXT_BODY}
              textMuted={TEXT_MUTED}
            />
          </Box>

          <ConfirmDeleteDialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            onConfirm={confirmDelete}
            itemType="user"
            itemName={user?.name}
            description="This action cannot be undone and will permanently remove all user data."
            confirmLabel="Delete User"
          />
        </Box>
      </Fade>
    </Container>
  );
};

export default UserDetails;